import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { request } from '../../Utils/request';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import TopNavBar from '../../Components/TopNavigation/TopNavBar';

type RecoveryMethod = 'email' | 'phone';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [recoveryMethod, setRecoveryMethod] = useState<RecoveryMethod>('phone');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleMethodChange = (_: React.MouseEvent<HTMLElement>, value: RecoveryMethod | null) => {
    if (value) {
      setRecoveryMethod(value);
      setError(null);
      setSuccess(null);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setPhoneNumber(value);
      setError(null);
      setSuccess(null);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value.trim());
    setError(null);
    setSuccess(null);
  };

  const isPhoneValid = phoneNumber.length === 10;
  const isEmailValid = emailRegex.test(email);
  const isFormValid = recoveryMethod === 'phone' ? isPhoneValid : isEmailValid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (recoveryMethod === 'phone') {
        const fullPhoneNumber = `+91${phoneNumber}`;
        await request<any, { phoneNumber: string }>('/auth/forgot-password/request', {
          method: 'POST',
          body: { phoneNumber: fullPhoneNumber },
        });
        // After OTP is sent, show OTP verify screen
        navigate('/forgot-password/otp', {
          state: { phoneNumber: fullPhoneNumber },
        });
      } else {
        await request<any, { email: string }>('/auth/forgot-password/request', {
          method: 'POST',
          body: { email },
        });
        // After OTP is sent, show OTP verify screen
        navigate('/forgot-password/otp', {
          state: { email, recoveryMethod: 'email' as const },
        });
      }
    } catch (err: any) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <TopNavBar title="Forgot Password" canGoBack={true} />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#ffffff', minHeight: '100vh', padding: '16px' }}>
        <Box style={{ width: '100%' }}>
          <Typography variant="h5" align="center" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
            Reset your password
          </Typography>

          <Typography variant="subtitle1" align="center" sx={{ mb: 2, color: 'text.secondary' }}>
            How would you like to reset your password?
          </Typography>

          <Box sx={{ width: '100%', mb: 3 }}>
            <ToggleButtonGroup
              value={recoveryMethod}
              exclusive
              onChange={handleMethodChange}
              aria-label="recovery method"
              sx={{
                width: '100%',
                '& .MuiToggleButton-root': {
                  flex: 1,
                  textTransform: 'none',
                  fontWeight: 600,
                  py: 1.5,
                },
                '& .Mui-selected': {
                  backgroundColor: '#FF6B35',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#e55a2b' },
                },
              }}
            >
              <ToggleButton value="email" aria-label="email">
                Email
              </ToggleButton>
              <ToggleButton value="phone" aria-label="phone">
                Phone
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Typography variant="subtitle1" align="center" sx={{ mb: 3, color: 'text.secondary' }}>
            {recoveryMethod === 'phone'
              ? "We'll send a verification code to your registered phone number"
              : "We'll send a reset link to your registered email address"}
          </Typography>

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {recoveryMethod === 'email' ? (
              <TextField
                name="email"
                label="Email address"
                type="email"
                value={email}
                onChange={handleEmailChange}
                required
                fullWidth
                placeholder="you@example.com"
              />
            ) : (
              <>
                <TextField
                  name="phoneNumber"
                  label="Phone Number"
                  type="tel"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  required
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">+91</InputAdornment>
                    ),
                  }}
                />
                <Typography variant="subtitle1" align="center" sx={{ color: 'text.secondary' }}>
                  Enter your 10-digit mobile number (Indian number only)
                </Typography>
              </>
            )}

            <Button
              type="submit"
              variant="contained"
              style={{
                marginTop: 8,
                backgroundColor: '#FF6B35',
                textTransform: 'none',
                borderRadius: 8,
                fontWeight: 600,
                padding: '12px 16px',
              }}
              fullWidth
              disabled={loading || !isFormValid}
            >
              {loading ? 'Sending...' : recoveryMethod === 'phone' ? 'Send Verification Code' : 'Send Reset Link'}
            </Button>
          </form>

          {success && <Alert severity="success" style={{ marginTop: 16 }}>{success}</Alert>}
          {error && <Alert severity="error" style={{ marginTop: 16 }}>{error}</Alert>}
        </Box>
      </div>
    </>
  );
};

export default ForgotPassword;

